import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, ExternalLink, DollarSign, Users, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const MONTHLY_PRODUCT_ID = "prod_TFVe92KUvlKJGg";
const ANNUAL_PRODUCT_ID = "prod_TFVe3ZSPeoxlVA";
const MONTHLY_PRICE = 3.99;
const ANNUAL_PRICE = 20.11;

// Apple IAP pricing (via RevenueCat) - for reference
const APPLE_MONTHLY_PRICE = 5.99;
const APPLE_ANNUAL_PRICE = 34.99;
const APPLE_LIFETIME_PRICE = 149.99;

interface CreatorCode {
  id: string;
  name: string;
  code: string;
  creator_email: string | null;
  notes: string | null;
  commission_rate: number;
  is_active: boolean;
  created_at: string;
  total_signups: number;
  total_revenue_generated: number;
  total_paid_out: number;
  last_payout_date: string | null;
}

interface CreatorPayout {
  id: string;
  amount: number;
  payout_date: string;
  notes: string | null;
  signups_count: number;
  total_revenue_at_payout: number;
}

const AdminCreatorCodes = () => {
  const { t } = useTranslation();

  const [codes, setCodes] = useState<CreatorCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CreatorCode | null>(null);
  const [payouts, setPayouts] = useState<CreatorPayout[]>([]);
  
  // Create dialog state
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newRate, setNewRate] = useState("0.25");
  const [creating, setCreating] = useState(false);
  
  // Payout dialog state
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('creator_referral_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCodes(data || []);
    } catch (error) {
      console.error("[AdminCreatorCodes] Error fetching codes:", error);
      toast.error(t('notifications.toast.failed_to_load_creator_codes'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim() || !newCode.trim() || !newEmail.trim()) {
      toast.error(t('notifications.toast.name_code_and_email_are_required'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error(t('notifications.toast.please_enter_a_valid_email_address'));
      return;
    }

    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0 || rate > 1) {
      toast.error(t('notifications.toast.commission_rate_must_be_between_0_and_1'));
      return;
    }

    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('creator_referral_codes')
        .insert({
          name: newName.trim(),
          code: newCode.trim().toUpperCase(),
          creator_email: newEmail.trim().toLowerCase(),
          notes: newNotes.trim() || null,
          commission_rate: rate,
          created_by: user?.id,
        });

      if (error) throw error;

      toast.success(t('notifications.toast.creator_code_created_successfully'));
      setShowCreateDialog(false);
      setNewName("");
      setNewCode("");
      setNewEmail("");
      setNewNotes("");
      setNewRate("0.25");
      fetchCodes();
    } catch (error: any) {
      console.error("[AdminCreatorCodes] Error creating code:", error);
      toast.error(error.message || "Failed to create creator code");
    } finally {
      setCreating(false);
    }
  };

  const handleMarkPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCode) return;
    
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('notifications.toast.please_enter_a_valid_payout_amount'));
      return;
    }

    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('creator_payouts')
        .insert({
          creator_code_id: selectedCode.id,
          amount: amount,
          notes: payoutNotes.trim() || null,
          paid_by: user?.id,
          signups_count: selectedCode.total_signups,
          total_revenue_at_payout: selectedCode.total_revenue_generated,
        });

      if (error) throw error;

      toast.success(t('notifications.toast.payout_recorded_successfully'));
      setShowPayoutDialog(false);
      setPayoutAmount("");
      setPayoutNotes("");
      setSelectedCode(null);
      fetchCodes();
    } catch (error: any) {
      console.error("[AdminCreatorCodes] Error recording payout:", error);
      toast.error(error.message || "Failed to record payout");
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = async (code: CreatorCode) => {
    setSelectedCode(code);
    setShowDetailsDialog(true);
    
    // Fetch payouts for this code
    try {
      const { data, error } = await supabase
        .from('creator_payouts')
        .select('*')
        .eq('creator_code_id', code.id)
        .order('payout_date', { ascending: false });

      if (error) throw error;
      setPayouts(data || []);
    } catch (error) {
      console.error("[AdminCreatorCodes] Error fetching payouts:", error);
      toast.error(t('notifications.toast.failed_to_load_payout_history'));
    }
  };

  const toggleCodeStatus = async (code: CreatorCode) => {
    try {
      const { error } = await supabase
        .from('creator_referral_codes')
        .update({ is_active: !code.is_active })
        .eq('id', code.id);

      if (error) throw error;
      
      toast.success(`Code ${code.is_active ? 'deactivated' : 'activated'}`);
      fetchCodes();
    } catch (error) {
      console.error("[AdminCreatorCodes] Error toggling status:", error);
      toast.error(t('notifications.toast.failed_to_update_code_status'));
    }
  };

  const updateCommissionRate = async (code: CreatorCode, newRate: string) => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0 || rate > 1) {
      toast.error(t('notifications.toast.rate_must_be_between_0_and_1'));
      return;
    }

    try {
      const { error } = await supabase
        .from('creator_referral_codes')
        .update({ commission_rate: rate })
        .eq('id', code.id);

      if (error) throw error;
      
      toast.success(t('notifications.toast.commission_rate_updated'));
      fetchCodes();
    } catch (error) {
      console.error("[AdminCreatorCodes] Error updating rate:", error);
      toast.error(t('notifications.toast.failed_to_update_commission_rate'));
    }
  };

  const calculateOwedAmount = (code: CreatorCode) => {
    const totalCommission = code.total_revenue_generated * code.commission_rate;
    const owed = totalCommission - code.total_paid_out;
    return Math.max(0, owed);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('ui.text.creator_referral_codes')}</CardTitle>
          <Button onClick={() => setShowCreateDialog(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />{t('ui.text.create_code')}</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : codes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_creator_codes_yet_create_one_to_get_started')}</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Signups</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Owed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-medium">{code.name}</TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {code.code}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {code.creator_email || "-"}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.05"
                        min="0"
                        max="1"
                        defaultValue={code.commission_rate}
                        onBlur={(e) => updateCommissionRate(code, e.target.value)}
                        className="w-20 h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        <Users className="h-3 w-3 mr-1" />
                        {code.total_signups}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {formatCurrency(code.total_revenue_generated)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(calculateOwedAmount(code))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={code.is_active}
                        onCheckedChange={() => toggleCodeStatus(code)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(code)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => {
                            setSelectedCode(code);
                            setShowPayoutDialog(true);
                          }}
                          disabled={calculateOwedAmount(code) === 0}
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          Pay
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create Code Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.text.create_creator_referral_code')}</DialogTitle>
            <DialogDescription>{t('ui.text.create_a_new_code_for_creators_influencers_users_w')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('ui.text.creator_name')}</Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('forms.placeholders.john_doe')}
                required
              />
            </div>
            <div>
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder={t('forms.placeholders.john2024')}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">{t('ui.text.code_will_be_converted_to_uppercase')}</p>
            </div>
            <div>
              <Label htmlFor="email">{t('ui.text.creator_email')}</Label>
              <Input
                id="email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('forms.placeholders.john_example_com')}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">{t('ui.text.creator_will_use_this_email_to_securely_access_the')}</p>
            </div>
            <div>
              <Label htmlFor="rate">{t('ui.text.commission_rate')}</Label>
              <Input
                id="rate"
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">{t('ui.text.default_0_25_25_of_recurring_revenue')}</p>
            </div>
            <div>
              <Label htmlFor="notes">{t('ui.text.notes_contact_info_etc')}</Label>
              <Textarea
                id="notes"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder={t('forms.placeholders.email_john_example_com_paypal_john_paypal_com')}
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? t('ui.text.creating') : t('ui.text.create_code')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payout for {selectedCode?.name}</DialogTitle>
            <DialogDescription>{t('ui.text.record_a_manual_payout_that_was_completed_outside_')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMarkPayout} className="space-y-4">
            <div>
              <Label>{t('ui.text.current_stats')}</Label>
              <div className="text-sm space-y-1 bg-muted p-3 rounded">
                <div>Total Signups: {selectedCode?.total_signups}</div>
                <div>Total Revenue: {formatCurrency(selectedCode?.total_revenue_generated)}</div>
                <div>Total Paid Out: {formatCurrency(selectedCode?.total_paid_out)}</div>
                <div className="font-semibold text-green-600">
                  Amount Owed: {selectedCode ? formatCurrency(calculateOwedAmount(selectedCode)) : formatCurrency(0)}
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="amount">{t('ui.text.payout_amount')}</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label htmlFor="payout-notes">{t('ui.text.notes_transaction_id_etc')}</Label>
              <Textarea
                id="payout-notes"
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder={t('forms.placeholders.paypal_transaction_id_xxx_date_nov_22_2025')}
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowPayoutDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? t('ui.text.recording') : t('ui.text.record_payout')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCode?.name} - Details</DialogTitle>
            <DialogDescription>{t('ui.text.view_comprehensive_statistics_and_payout_history')}</DialogDescription>
          </DialogHeader>
          
          {selectedCode && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground">{t('ui.text.total_signups')}</div>
                  <div className="text-2xl font-bold">{selectedCode.total_signups}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground">{t('ui.text.commission_rate')}</div>
                  <div className="text-2xl font-bold">{(selectedCode.commission_rate * 100).toFixed(0)}%</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground">{t('ui.text.total_revenue')}</div>
                  <div className="text-2xl font-bold">${selectedCode.total_revenue_generated.toFixed(2)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-muted-foreground">{t('ui.text.amount_owed')}</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${calculateOwedAmount(selectedCode).toFixed(2)}
                  </div>
                </Card>
              </div>

              {/* Contact Info */}
              {selectedCode.notes && (
                <div>
                  <h3 className="font-semibold mb-2">{t('ui.text.contact_info')}</h3>
                  <div className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
                    {selectedCode.notes}
                  </div>
                </div>
              )}

              {/* Payout History */}
              <div>
                <h3 className="font-semibold mb-2">{t('ui.text.payout_history')}</h3>
                {payouts.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">{t('ui.text.no_payouts_recorded_yet')}</div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>{t('ui.text.signups_at_time')}</TableHead>
                          <TableHead>{t('ui.text.revenue_at_time')}</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payouts.map((payout) => (
                          <TableRow key={payout.id}>
                            <TableCell>
                              {new Date(payout.payout_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="font-semibold">
                              ${payout.amount.toFixed(2)}
                            </TableCell>
                            <TableCell>{payout.signups_count}</TableCell>
                            <TableCell>${payout.total_revenue_at_payout.toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {payout.notes || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminCreatorCodes;
